// Rechner: Passt das Möbelstück durch Tür, Flur, Aufzug oder Treppenhaus?
// Interaktiver visueller Editor nach dem Claude-Design (ziehbare SVG-Skizze).
//
// WICHTIG: Das Urteil (passt/knapp/nicht/unsicher), die Zusammenfassung, kritische
// Maße, Warnungen und Tipps kommen ausschließlich aus src/lib/calc.js (checkFit).
// Hier wird nur zusätzliche Zeichen-Geometrie abgeleitet (Querschnitt, Grundfläche,
// Skalierung) — die ändert das Verdikt nicht. Ziehen ändert nur Eingabewerte.
import React, { useEffect, useMemo, useState } from "react";
import { RotateCcw, Copy, Printer, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { checkFit, STATUS_LABEL } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { startDrag, round1, clamp } from "@/components/preview/useDragSvg";
import { C, ST, stKey, Svg, handle, hDim, vDim, pill, fpLabel, fmtN, placeholder } from "@/components/preview/canvasPrimitives";

const W = 880;
const H = 470;

// ---------------------------------------------------------------------------
// Objektpresets (Default-Maße + Messhinweis) — aus dem Design übernommen.
// ---------------------------------------------------------------------------
const OBJECTS = [
  { id: "sofa", label: "Sofa", dims: { width: "200", height: "85", depth: "95" }, hint: "Beim Sofa die größten Außenmaße inkl. Armlehnen, Rückenlehne und Füßen messen. Sind die Füße abnehmbar, rechne einmal mit und einmal ohne." },
  { id: "sessel", label: "Sessel", dims: { width: "90", height: "100", depth: "90" }, hint: "Breiteste Stelle inkl. Armlehnen messen. Sessel lassen sich oft kippen." },
  { id: "schrank", label: "Schrank", dims: { width: "100", height: "200", depth: "60" }, hint: "Bei Schränken ist die Höhe oft kritisch. Sockel und Kranz mitmessen; Türen und Schubladen lassen sich meist abnehmen." },
  { id: "kommode", label: "Kommode", dims: { width: "80", height: "85", depth: "45" }, hint: "Griffe und überstehende Teile mitmessen. Schubladen herausnehmen macht die Kommode leichter." },
  { id: "matratze", label: "Matratze", dims: { width: "140", height: "22", depth: "200" }, hint: "Matratzen lassen sich oft leicht biegen oder hochkant tragen. Die Dicke ist meist unkritisch." },
  { id: "waschmaschine", label: "Waschmaschine", dims: { width: "60", height: "85", depth: "62" }, hint: "Tür, Schläuche und Anschlüsse beachten. Mit Sackkarre und Spanngurt sicher transportieren." },
  { id: "kuehlschrank", label: "Kühlschrank", dims: { width: "60", height: "185", depth: "65" }, hint: "Griffe und Türen abnehmen spart oft mehrere Zentimeter Breite. Aufrecht transportieren." },
  { id: "tisch", label: "Tisch", dims: { width: "160", height: "75", depth: "90" }, hint: "Beine lassen sich meist abschrauben, dann zählt nur die Tischplatte (Länge x Breite x Dicke)." },
  { id: "sonstiges", label: "Sonstiges", dims: { width: "100", height: "100", depth: "60" }, hint: "Miss immer die größten Außenmaße inkl. Griffe, Füße und überstehende Teile." },
];

const OBSTACLES = [
  { id: "door", label: "Tür" },
  { id: "hallway", label: "Flur" },
  { id: "elevator", label: "Aufzug" },
  { id: "staircase", label: "Treppenhaus" },
];

const CLEARANCES = [
  { id: "5", label: "locker" },
  { id: "3", label: "normal" },
  { id: "1", label: "knapp" },
];

// Engstellen-Felder + Titel je Modus.
const OBSTACLE_FIELDS = {
  door: { title: "Türmaße", fields: [
    { id: "width", label: "Türbreite", step: "1" },
    { id: "height", label: "Türhöhe", step: "1" },
  ] },
  hallway: { title: "Flurmaße", fields: [
    { id: "width", label: "Flurbreite", step: "1" },
    { id: "height", label: "Freie Höhe (optional)", step: "1" },
  ] },
  elevator: { title: "Aufzugmaße", fields: [
    { id: "doorWidth", label: "Türbreite", step: "1" },
    { id: "innerWidth", label: "Innenbreite", step: "1" },
    { id: "innerDepth", label: "Innentiefe", step: "1" },
    { id: "innerHeight", label: "Innenhöhe", step: "1" },
  ] },
  staircase: { title: "Treppenhausmaße", fields: [
    { id: "stairWidth", label: "Schmalste Treppenbreite", step: "1" },
    { id: "landingWidth", label: "Podestbreite", step: "1" },
    { id: "landingDepth", label: "Podesttiefe", step: "1" },
    { id: "clearHeight", label: "Lichte Höhe (optional)", step: "1" },
  ] },
};

const OBSTACLE_DEFAULTS = {
  door: { width: "90", height: "200" },
  hallway: { width: "110", height: "240" },
  elevator: { doorWidth: "80", innerWidth: "100", innerDepth: "140", innerHeight: "210" },
  staircase: { stairWidth: "100", landingWidth: "120", landingDepth: "120", clearHeight: "220" },
};

const TOOLBAR_HINT = {
  door: "Ziehe die Anfasser an der Tür, oder justiere die Maße unten. Das Möbelstück wird nach Ergebnis eingefärbt.",
  hallway: "Ziehe die Wand, um die Flurbreite zu ändern. Die Draufsicht zeigt das Möbelstück im Flur.",
  elevator: "Ziehe an Kabine und Aufzugtür. Bei 'Diagonal stellen' wird das Möbel über Eck gestellt.",
  staircase: "Treppenhaus ist der schwierigste Fall. Die Skizze ist eine grobe Orientierung, kein Beweis.",
};

function toNum(s) {
  const x = parseFloat(String(s).replace(",", "."));
  return isNaN(x) ? NaN : x;
}

export default function FitCalculator() {
  const [objectId, setObjectId] = useState("sofa");
  const [furniture, setFurniture] = useState(OBJECTS[0].dims);
  const [obstacleType, setObstacleType] = useState("door");
  const [obstacles, setObstacles] = useState(OBSTACLE_DEFAULTS);
  const [clearance, setClearance] = useState("3");
  const [diag, setDiag] = useState(true); // "Diagonal stellen" für Aufzug
  const [copied, setCopied] = useState(false);

  const obj = OBJECTS.find((o) => o.id === objectId) || OBJECTS[0];
  const obstacle = obstacles[obstacleType];

  const selectObject = (id) => {
    setObjectId(id);
    const o = OBJECTS.find((x) => x.id === id);
    if (o) setFurniture(o.dims);
  };

  const setF = (k) => (val) => setFurniture((s) => ({ ...s, [k]: val }));
  const setFNow = (k, val) => setFurniture((s) => ({ ...s, [k]: val }));
  const setO = (k) => (val) => setObstacles((s) => ({ ...s, [obstacleType]: { ...s[obstacleType], [k]: val } }));
  const setONow = (k, val) => setObstacles((s) => ({ ...s, [obstacleType]: { ...s[obstacleType], [k]: val } }));

  const reset = () => {
    setFurniture(obj.dims);
    setObstacles((s) => ({ ...s, [obstacleType]: OBSTACLE_DEFAULTS[obstacleType] }));
    setClearance("3");
    setDiag(true);
  };

  // Verdikt: einzige Wahrheit ist calc.js.
  const r = useMemo(
    () => checkFit(obstacleType, furniture, obstacle, Number(clearance)),
    [obstacleType, furniture, obstacle, clearance]
  );
  const skey = stKey(r.status);
  const st = ST[skey] || ST.uncertain;

  // Analytics: calculator_used + result_shown nach jedem Verdikt.
  useEffect(() => {
    if (!r.ok) return;
    const passage = obstacleType;
    const mode = Number(clearance) > 0 ? "with_reserve" : "tight";
    const verdict = r.status;
    const fw = parseFloat(String(furniture.width).replace(",", "."));
    const width_bucket = isNaN(fw)
      ? undefined
      : fw < 60 ? "<60" : fw <= 80 ? "60-80" : fw <= 100 ? "80-100" : ">100";
    trackSettled("calculator_used", { calculator: passage, mode });
    trackSettled("result_shown", width_bucket
      ? { calculator: passage, verdict, width_bucket }
      : { calculator: passage, verdict });
  }, [r, obstacleType, clearance, furniture.width]);

  // Zeichen-Geometrie (nur Optik, nicht Verdikt) — diagonal-Toggle wirkt nur visuell.
  const canvas = useMemo(
    () => renderCanvas(obstacleType, furniture, obstacle, skey, st, diag, setFNow, setONow),
    // setFNow/setONow sind stabile State-Updater-Wrapper; Re-Render hängt an den Werten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [obstacleType, furniture, obstacle, skey, st, diag]
  );

  const keyMetric = keyMetricFor(obstacleType, furniture, obstacle);

  const copyText = r.ok
    ? [
        `Ergebnis: ${STATUS_LABEL[r.status]}`,
        r.summary,
        ...r.criticalMeasurements.map((c) => `Kritisch: ${c}`),
        ...r.tips.map((t) => `Tipp: ${t}`),
      ].join("\n")
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = copyText;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    track("result_action", { action: "copy" });
  };

  return (
    <div data-testid="calc-fit" className="space-y-4">
      {/* Objektauswahl */}
      <div className="no-print">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Möbelstück</p>
        <div className="flex flex-wrap gap-2">
          {OBJECTS.map((o) => (
            <Button
              key={o.id}
              type="button"
              size="sm"
              variant={objectId === o.id ? "default" : "outline"}
              onClick={() => selectObject(o.id)}
              data-testid={`object-${o.id}`}
            >
              {o.label}
            </Button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{obj.hint}</p>
      </div>

      {/* Engstellen-Tabs */}
      <div role="tablist" className="no-print inline-flex flex-wrap gap-1 rounded-xl border bg-secondary/40 p-1">
        {OBSTACLES.map((o) => (
          <Button
            key={o.id}
            type="button"
            size="sm"
            variant={obstacleType === o.id ? "default" : "ghost"}
            onClick={() => setObstacleType(o.id)}
            data-testid={`obstacle-${o.id}`}
          >
            {o.label}
          </Button>
        ))}
      </div>

      {/* Ergebnis-Banner */}
      {r.ok && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3"
          style={{ background: st.bg, borderColor: st.border }}
          data-testid="calc-result"
        >
          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: st.dot }} />
          <div className="min-w-0 flex-1">
            <p className="font-heading font-semibold text-lg leading-tight" style={{ color: st.text }} data-testid="result-status">
              {STATUS_LABEL[r.status]}
            </p>
            <p className="mt-1 text-sm leading-snug text-foreground/80">{r.summary}</p>
          </div>
          {keyMetric && (
            <div className="rounded-lg border px-3 py-1.5 text-right" style={{ borderColor: st.border }}>
              <p className="text-[11px] font-semibold uppercase tracking-wide opacity-75" style={{ color: st.text }}>{keyMetric.label}</p>
              <p className="font-heading font-semibold tabular-nums" style={{ color: st.text }}>{keyMetric.value}</p>
            </div>
          )}
        </div>
      )}

      {/* Editor-Canvas */}
      <div className="overflow-hidden rounded-2xl border bg-card">
        {/* Toolbar */}
        <div className="no-print flex flex-wrap items-center justify-between gap-3 border-b bg-secondary/40 px-4 py-3">
          <span className="max-w-[42ch] text-xs leading-snug text-muted-foreground">{TOOLBAR_HINT[obstacleType]}</span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground" title="Wie viel Reserve über die nackten Maße hinaus eingeplant wird. Mehr Reserve = strengere Bewertung.">Reserve</span>
              <div className="inline-flex rounded-lg border bg-background p-0.5" data-testid="clearance-toggle">
                {CLEARANCES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setClearance(c.id)}
                    data-testid={`clearance-${c.id}`}
                    className={`h-7 rounded-md px-2.5 text-xs font-semibold ${clearance === c.id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <span className="text-[11px] font-semibold text-primary whitespace-nowrap">{clearance} cm</span>
            </div>
            {obstacleType === "elevator" && (
              <Button
                type="button"
                variant={diag ? "default" : "outline"}
                size="sm"
                onClick={() => setDiag((d) => !d)}
                className="h-8"
                data-testid="btn-diagonal"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Diagonal stellen
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={reset} className="h-8" data-testid="btn-reset">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Zurücksetzen
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="px-2 pb-1 pt-2 sm:px-4">{canvas}</div>

        {/* Legende */}
        <div className="no-print flex flex-wrap gap-x-5 gap-y-2 border-t px-4 py-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-4 w-4 rounded border-2" style={{ borderColor: C.objStroke, background: C.obj }} /> Engstelle
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-4 w-4 rounded border-2" style={{ borderColor: st.stroke, background: st.fill }} /> Möbelstück (nach Ergebnis eingefärbt)
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-4 w-4 rounded-full border-2 bg-white" style={{ borderColor: C.primary }} /> Anfasser zum Ziehen
          </span>
        </div>
      </div>

      {/* Maße + Vergleich */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Möbel- und Engstellenmaße */}
        <div className="rounded-2xl border bg-card p-4 sm:p-5">
          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Möbelmaße</h3>
          <p className="text-xs text-muted-foreground mb-3">Größte Außenmaße in cm.</p>
          <div className="grid grid-cols-3 gap-3">
            <Field id="f-width" label="Breite" value={furniture.width} onChange={setF("width")} />
            <Field id="f-height" label="Höhe" value={furniture.height} onChange={setF("height")} />
            <Field id="f-depth" label="Tiefe" value={furniture.depth} onChange={setF("depth")} />
          </div>

          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-5 mb-3">
            {OBSTACLE_FIELDS[obstacleType].title}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {OBSTACLE_FIELDS[obstacleType].fields.map((f) => (
              <Field
                key={f.id}
                id={`o-${f.id}`}
                label={f.label}
                value={obstacle[f.id] ?? ""}
                onChange={setO(f.id)}
                step={f.step}
              />
            ))}
          </div>
        </div>

        {/* Maße im Vergleich (Ergebnis-Details) */}
        <div className="rounded-2xl border bg-card p-4 sm:p-5">
          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Maße im Vergleich</h3>

          {r.warnings.length > 0 && (
            <div className="mb-3 rounded-xl border border-warning/40 bg-warning/10 p-3 space-y-1.5" role="alert" data-testid="calc-warnings">
              {r.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {r.ok ? (
            <>
              {r.criticalMeasurements.length > 0 && (
                <CompareRow label="Kritisches Maß" value={r.criticalMeasurements.join(", ")} color={st.stroke} />
              )}
              {r.possibleOrientations.length > 0 && (
                <CompareRow label="Mögliche Orientierung" value={r.possibleOrientations.join("; ")} />
              )}
              {keyMetric && <CompareRow label={keyMetric.label} value={keyMetric.value} color={st.stroke} />}
              <CompareRow label="Eingeplante Reserve" value={`${clearance} cm`} />

              <div className="no-print mt-4 flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={copy} data-testid="btn-copy">
                  {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
                  {copied ? "Kopiert" : "Ergebnis kopieren"}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { track("result_action", { action: "print" }); window.print(); }} data-testid="btn-print">
                  <Printer className="mr-1.5 h-4 w-4" /> Drucken
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{r.summary}</p>
          )}
        </div>
      </div>

      {/* Tipps & Checkliste */}
      {r.ok && r.tips.length > 0 && (
        <div className="rounded-2xl border bg-card p-4 sm:p-5">
          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Tipps & Checkliste</h3>
          <ol className="space-y-2.5" data-testid="calc-steps">
            {r.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                  {i + 1}
                </span>
                <span className="leading-relaxed text-muted-foreground">{t}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <p className="px-1 pt-1 text-center text-[11px] leading-relaxed text-muted-foreground">
        Der Rechner gibt eine praktische Orientierung, keine Garantie. Miss vor dem Transport immer selbst nach, plane
        Spielraum ein und prüfe Türrahmen, Schwellen, Geländer und Kurven vor Ort.
      </p>
    </div>
  );
}

// --- kleine Bausteine ------------------------------------------------------
function Field({ id, label, value, onChange, step = "1" }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 block text-sm font-medium">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={`field-${id}`}
          type="number"
          inputMode="decimal"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 pr-10 text-base tabular-nums"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">cm</span>
      </div>
    </div>
  );
}

function CompareRow({ label, value, color }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-0">
      <span className="text-sm text-muted-foreground leading-snug">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-right" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

// Kennzahl im Banner/Vergleich: das jeweils kritische Maß je Modus.
function keyMetricFor(mode, f, o) {
  const fw = toNum(f.width), fh = toNum(f.height), fd = toNum(f.depth);
  if (![fw, fh, fd].every((x) => x > 0)) return null;
  if (mode === "door") {
    const dW = toNum(o.width);
    if (!(dW > 0)) return null;
    const minFace = Math.min(fw, fh, fd);
    return { label: "Türbreite vs. Möbel", value: `${fmtN(dW)} / ${fmtN(minFace)} cm` };
  }
  if (mode === "hallway") {
    const hW = toNum(o.width);
    if (!(hW > 0)) return null;
    const across = Math.min(fw, fd);
    return { label: "Flur vs. Querschnitt", value: `${fmtN(hW)} / ${fmtN(across)} cm` };
  }
  if (mode === "elevator") {
    const eDoor = toNum(o.doorWidth);
    if (!(eDoor > 0)) return null;
    const fpW = Math.min(fw, fd);
    return { label: "Aufzugtür vs. Möbel", value: `${fmtN(eDoor)} / ${fmtN(fpW)} cm` };
  }
  if (mode === "staircase") {
    const sStair = toNum(o.stairWidth);
    if (!(sStair > 0)) return null;
    const fpW = Math.min(fw, fd);
    return { label: "Treppe vs. Querschnitt", value: `${fmtN(sStair)} / ${fmtN(fpW)} cm` };
  }
  return null;
}

// ===========================================================================
// CANVAS-RENDERER (reine Optik). Geometrie spiegelt das Claude-Design,
// die Einfärbung folgt dem calc.js-Status (skey/st).
// ===========================================================================
function renderCanvas(mode, f, o, skey, st, diag, setFNow, setONow) {
  const fw = toNum(f.width), fh = toNum(f.height), fd = toNum(f.depth);
  if (mode === "door") return renderDoor(fw, fh, fd, o, st, setONow);
  if (mode === "hallway") return renderHall(fw, fh, fd, o, st, setONow);
  if (mode === "elevator") return renderElev(fw, fh, fd, o, st, diag, setONow);
  if (mode === "staircase") return renderStair(fw, fh, fd, o, st, setONow);
  return placeholder("Maße eingeben, um die Vorschau zu sehen.");
}

// Beste geradlinige Orientierung für die Tür-Ansicht (nur Optik).
function bestFace(fw, fh, fd, dW, dH) {
  const faces = [
    { a: fw, b: fh },
    { a: fw, b: fd },
    { a: fh, b: fd },
  ];
  let best = null;
  faces.forEach((face) => {
    [[face.a, face.b], [face.b, face.a]].forEach(([pw, ph]) => {
      const m = Math.min(dW - pw, dH - ph);
      if (!best || m > best.m) best = { faceW: pw, faceH: ph, m };
    });
  });
  return best;
}

function renderDoor(fw, fh, fd, o, st, setONow) {
  const dW = toNum(o.width), dH = toNum(o.height);
  if (![fw, fh, fd, dW, dH].every((x) => x > 0)) return placeholder("Möbel- und Türmaße eingeben, um die Vorschau zu sehen.");
  const face = bestFace(fw, fh, fd, dW, dH);
  const drawFW = face.faceW > 0 ? face.faceW : Math.min(fw, fd);
  const drawFH = face.faceH > 0 ? face.faceH : fh;
  const floorY = H - 72, padTop = 58, sw = 46;
  const availW = 470, availH = floorY - padTop;
  const scale = Math.min(availW / (Math.max(dW, drawFW) * 1.06), availH / (Math.max(dH, drawFH) * 1.04));
  const cx = W / 2;
  const dpw = dW * scale, dph = dH * scale, dx = cx - dpw / 2, dy = floorY - dph;
  const fpw = drawFW * scale, fph = drawFH * scale, fx = cx - fpw / 2, fy = floorY - fph;
  const wallTop = padTop - 4;
  const els = [];
  els.push(<rect key="wl" x={dx - sw} y={wallTop} width={sw} height={floorY - wallTop} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.4} />);
  els.push(<rect key="wr" x={dx + dpw} y={wallTop} width={sw} height={floorY - wallTop} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.4} />);
  els.push(<rect key="lt" x={dx - sw} y={wallTop} width={dpw + 2 * sw} height={Math.max(0, dy - wallTop)} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.4} />);
  els.push(<rect key="open" x={dx} y={dy} width={dpw} height={dph} fill="#fff" stroke={C.objStroke} strokeWidth={2} />);
  els.push(<line key="floor" x1={dx - sw - 22} y1={floorY} x2={dx + dpw + sw + 22} y2={floorY} stroke={C.floor} strokeWidth={2.5} />);
  els.push(<rect key="furn" x={fx} y={fy} width={Math.max(3, fpw)} height={Math.max(3, fph)} rx={4} fill={st.fill} stroke={st.stroke} strokeWidth={2.4} />);
  els.push(fpLabel("fl", cx, fy + fph / 2, st.stroke, "Querschnitt"));
  els.push(hDim("dw", dx, dx + dpw, floorY + 22, "Tür " + fmtN(dW), C.dim, W));
  els.push(vDim("dh", floorY, dy, dx - sw - 20, fmtN(dH), C.dim, W));
  els.push(hDim("fwd", fx, fx + fpw, fy - 18, fmtN(drawFW), st.stroke, W));
  els.push(vDim("fhd", fy, fy + fph, fx + fpw + 22 < dx + dpw ? fx + fpw + 22 : dx + dpw + sw + 20, fmtN(drawFH), st.stroke, W));
  els.push(handle("hw", dx + dpw, dy + dph / 2, "ew-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("width", String(round1(clamp((cur.x - cx) * 2 / scale, 40, 160))))), "v"));
  els.push(handle("hh", cx, dy, "ns-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("height", String(round1(clamp((floorY - cur.y) / scale, 120, 270))))), "h"));
  return <Svg vbW={W} vbH={H} ariaLabel="Vorschau Tür: Möbel-Querschnitt vs. Türöffnung">{els}</Svg>;
}

function renderHall(fw, fh, fd, o, st, setONow) {
  const hW = toNum(o.width), hH = toNum(o.height);
  if (![fw, fh, fd, hW].every((x) => x > 0)) return placeholder("Möbel- und Flurmaße eingeben, um die Vorschau zu sehen.");
  const across = Math.min(fw, fd), along = Math.max(fw, fd);
  const padL = 66, padR = W - 70, midY = H / 2 - 4;
  const scale = Math.min(150 / (Math.max(hW, across) * 1.15), (padR - padL) * 0.62 / along);
  const corrH = hW * scale, ct = midY - corrH / 2, cb = midY + corrH / 2;
  const fL = along * scale, fW2 = across * scale, cx = (padL + padR) / 2;
  const hasH = hH > 0;
  const els = [];
  els.push(<rect key="wt" x={padL} y={36} width={padR - padL} height={ct - 36} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.4} />);
  els.push(<rect key="wb" x={padL} y={cb} width={padR - padL} height={H - 64 - cb} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.4} />);
  els.push(<line key="cl1" x1={padL} y1={ct} x2={padR} y2={ct} stroke={C.objStroke} strokeWidth={2} />);
  els.push(<line key="cl2" x1={padL} y1={cb} x2={padR} y2={cb} stroke={C.objStroke} strokeWidth={2} />);
  els.push(<line key="ar" x1={cx - fL / 2 - 30} y1={midY} x2={padR - 14} y2={midY} stroke={C.guide} strokeWidth={1.4} strokeDasharray="4 5" />);
  els.push(<path key="arh" d={"M" + (padR - 14) + " " + midY + " l-9 -5 l0 10 z"} fill={C.guide} />);
  els.push(<rect key="furn" x={cx - fL / 2} y={midY - fW2 / 2} width={fL} height={fW2} rx={4} fill={st.fill} stroke={st.stroke} strokeWidth={2.4} />);
  els.push(fpLabel("fl", cx, midY, st.stroke, "Draufsicht"));
  els.push(vDim("hw", ct, cb, padL + 16, "Flur " + fmtN(hW), st.stroke, W));
  els.push(vDim("fa", midY - fW2 / 2, midY + fW2 / 2, cx + fL / 2 + 22, fmtN(across), st.stroke, W));
  els.push(hDim("fl2", cx - fL / 2, cx + fL / 2, midY - fW2 / 2 - 16, fmtN(along), C.dim, W));
  if (hasH) els.push(pill("hn", padR - 8, 50, "Höhe Möbel " + fmtN(fh) + " / frei " + fmtN(hH), C.dim, "end", W));
  els.push(handle("hw1", cx, cb, "ns-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("width", String(round1(clamp((cur.y - midY) * 2 / scale, 40, 320))))), "h"));
  return <Svg vbW={W} vbH={H} ariaLabel="Vorschau Flur: Draufsicht Möbel im Gang">{els}</Svg>;
}

function renderElev(fw, fh, fd, o, st, diag, setONow) {
  const eDoor = toNum(o.doorWidth), eW = toNum(o.innerWidth), eD = toNum(o.innerDepth), eH = toNum(o.innerHeight);
  if (![fw, fh, fd, eDoor, eW, eD].every((x) => x > 0)) return placeholder("Möbel- und Aufzugmaße eingeben, um die Vorschau zu sehen.");
  const fpW = Math.min(fw, fd), fpL = Math.max(fw, fd);
  const cx = W / 2 - 20, cy = H / 2 + 2;
  const scale = Math.min(440 / (eW * 1.2), (H - 150) / (eD * 1.25));
  const cpw = eW * scale, cph = eD * scale, bx = cx - cpw / 2, by = cy - cph / 2;
  // Orientierung der Grundfläche: gerade oder (bei aktivem Diagonal-Toggle) über Eck.
  const o1 = Math.min(eW - fpW, eD - fpL), o2 = Math.min(eD - fpW, eW - fpL);
  let angle = o1 >= o2 ? 0 : 90;
  const floorDiag = Math.sqrt(eW * eW + eD * eD);
  if (Math.max(o1, o2) < 0 && diag && fpL + 1 <= floorDiag && fpW < Math.min(eW, eD)) {
    angle = (Math.atan2(eD, eW) * 180) / Math.PI;
  }
  const gpw = eDoor * scale, gx = cx - gpw / 2, gy = by + cph;
  const els = [];
  els.push(<rect key="cab" x={bx} y={by} width={cpw} height={cph} rx={4} fill={C.wall} stroke={C.objStroke} strokeWidth={2} />);
  els.push(<rect key="gapbg" x={gx} y={gy - 3} width={gpw} height={6} fill="#fff" />);
  els.push(<line key="jl" x1={bx} y1={gy} x2={gx} y2={gy} stroke={C.objStroke} strokeWidth={2.5} />);
  els.push(<line key="jr" x1={gx + gpw} y1={gy} x2={bx + cpw} y2={gy} stroke={C.objStroke} strokeWidth={2.5} />);
  const fpwPx = fpL * scale, fphPx = fpW * scale;
  els.push(
    <g key="furn" transform={"rotate(" + angle + " " + cx + " " + cy + ")"}>
      <rect x={cx - fpwPx / 2} y={cy - fphPx / 2} width={fpwPx} height={fphPx} rx={4} fill={st.fill} stroke={st.stroke} strokeWidth={2.4} />
    </g>
  );
  els.push(fpLabel("fl", cx, cy, st.stroke, "Grundfläche"));
  els.push(hDim("ew", bx, bx + cpw, by - 16, "Breite " + fmtN(eW), C.dim, W));
  els.push(vDim("ed", by, by + cph, bx - 24, "Tiefe " + fmtN(eD), C.dim, W));
  els.push(hDim("edoor", gx, gx + gpw, gy + 30, "Tür " + fmtN(eDoor), st.stroke, W));
  if (eH > 0) els.push(pill("hn", W - 14, 44, "Höhe Möbel " + fmtN(fh) + " / innen " + fmtN(eH), C.dim, "end", W));
  els.push(handle("hew", bx + cpw, cy, "ew-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("innerWidth", String(round1(clamp((cur.x - cx) * 2 / scale, 50, 250))))), "v"));
  els.push(handle("hed", cx, by, "ns-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("innerDepth", String(round1(clamp((cy - cur.y) * 2 / scale, 60, 300))))), "h"));
  els.push(handle("hdoor", gx + gpw, gy, "ew-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("doorWidth", String(round1(clamp((cur.x - cx) * 2 / scale, 40, eW))))), "v"));
  return <Svg vbW={W} vbH={H} ariaLabel="Vorschau Aufzug: Grundfläche der Kabine">{els}</Svg>;
}

function renderStair(fw, fh, fd, o, st, setONow) {
  const sStair = toNum(o.stairWidth), sLW = toNum(o.landingWidth), sLD = toNum(o.landingDepth), sH = toNum(o.clearHeight);
  if (![fw, fh, fd, sStair, sLW, sLD].every((x) => x > 0)) return placeholder("Möbel-, Treppen- und Podestmaße eingeben, um die Vorschau zu sehen.");
  const fpW = Math.min(fw, fd), longest = Math.max(fw, fh, fd), diagCm = Math.sqrt(sLW * sLW + sLD * sLD);
  const cornerX = 250, cornerY = 366;
  let scale = Math.min(
    286 / sLW,
    226 / sLD,
    ((W - 90 - cornerX) * diagCm) / (longest * sLW),
    ((cornerY - 54) * diagCm) / (longest * sLD)
  );
  if (!isFinite(scale) || scale <= 0) scale = Math.min(286 / sLW, 226 / sLD);
  const pw = sLW * scale, ph = sLD * scale;
  const bx = cornerX, by = cornerY - ph;
  const els = [];
  // Podest (Draufsicht).
  els.push(<rect key="land" x={bx} y={by} width={pw} height={ph} rx={3} fill={C.wall} stroke={C.objStroke} strokeWidth={2} />);
  // Zwei Treppenläufe, die am Podest 180° wenden (nur Andeutung).
  const flW = Math.max(16, Math.min(sStair * scale, (pw - 12) / 2 - 2));
  const cxm = bx + pw / 2;
  els.push(<rect key="fl-r" x={cxm + 6} y={by - 46} width={flW} height={46} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.2} />);
  els.push(<rect key="fl-l" x={cxm - 6 - flW} y={by - 46} width={flW} height={46} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.2} />);
  els.push(pill("plw", cxm, by - 23, "Treppe " + fmtN(sStair), C.dim, "middle", W));
  // Möbel entlang der Podest-Diagonale (Drehfläche).
  const mx = bx + pw / 2, my = by + ph / 2;
  const angleDeg = (Math.atan2(-ph, pw) * 180) / Math.PI;
  const objW = longest * scale, objH = fpW * scale;
  els.push(
    <g key="furn" transform={"rotate(" + angleDeg + " " + mx + " " + my + ")"}>
      <rect x={mx - objW / 2} y={my - objH / 2} width={objW} height={Math.max(3, objH)} rx={4} fill={st.fill} stroke={st.stroke} strokeWidth={2.4} />
    </g>
  );
  // Podest-Diagonale als gestrichelte Hilfslinie.
  els.push(<line key="dl" x1={bx} y1={by + ph} x2={bx + pw} y2={by} stroke={C.guide} strokeWidth={1.2} strokeDasharray="4 5" />);
  els.push(fpLabel("fl", mx, my - objH / 2 - 16, st.stroke, "längstes Maß " + fmtN(longest)));
  // Podestmaße.
  els.push(hDim("lw", bx, bx + pw, by + ph + 22, "Podest " + fmtN(sLW), C.dim, W));
  els.push(vDim("ld", by, by + ph, bx - 22, fmtN(sLD), C.dim, W));
  els.push(pill("diag", bx + pw + 14, by + ph / 2, "Diagonale " + fmtN(diagCm), st.stroke, "start", W));
  if (sH > 0) els.push(pill("hn", W - 14, 40, "Höhe Möbel " + fmtN(fh) + " / lichte " + fmtN(sH), C.dim, "end", W));
  // Drag: Podestbreite/-tiefe.
  els.push(handle("hlw", bx + pw, by + ph, "ew-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("landingWidth", String(round1(clamp((cur.x - bx) / scale, 40, 300))))), "v"));
  els.push(handle("hld", bx, by, "ns-resize", (ev) => startDrag(ev, W, H, (cur) => setONow("landingDepth", String(round1(clamp((cornerY - cur.y) / scale, 40, 300))))), "h"));
  return <Svg vbW={W} vbH={H} ariaLabel="Vorschau Treppenhaus: Podest-Drehfläche (grobe Orientierung)">{els}</Svg>;
}
